import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../config/axios';
import CustomerLayout from '../../components/Layout/CustomerLayout';
import { useLanguage } from '../../context/LanguageContext';
import { BASE_URL } from '../../config/environment';
import {
    MdCheckCircle,
    MdPending,
    MdCancel,
    MdUpload,
    MdDownload,
    MdDelete,
    MdExpandMore,
    MdClose,
    MdSend,
    MdComment,
    MdSchedule
} from 'react-icons/md';
import { FaHome, FaUser, FaHistory } from 'react-icons/fa';
import { BsChatRightText } from "react-icons/bs";

// File URL helper fonksiyonu
const getFileUrl = (filePath) => {
  if (!filePath) return null;
  // Eğer filePath zaten tam URL ise (http ile başlıyorsa) direkt döndür
  if (filePath.startsWith('http')) {
    return filePath;
  }
  // Eğer relative path ise BASE_URL ile birleştir
  return `${BASE_URL}${filePath}`;
};

const CustomerSupportCaseDetailPage = () => {
    const { t } = useLanguage();
    const { id } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [fileDescription, setFileDescription] = useState('');
    const fileInputRef = useRef(null);
    const [isUploadSectionExpanded, setIsUploadSectionExpanded] = useState(true);
    const [popup, setPopup] = useState({
        show: false,
        title: '',
        message: '',
        type: 'info',
        showCancel: false,
        onConfirm: null,
        onCancel: null
    });
    const [activities, setActivities] = useState([]);
    const [isActivitiesLoading, setIsActivitiesLoading] = useState(true);
    const [isActivitiesSectionExpanded, setIsActivitiesSectionExpanded] = useState(true);
    const [activityMessage, setActivityMessage] = useState('');
         const [isSubmittingActivity, setIsSubmittingActivity] = useState(false);
     const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
     const [showActivityPopup, setShowActivityPopup] = useState(false);

    useEffect(() => {
        fetchCaseData();
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(userData);
    }, [id]);

    useEffect(() => {
        if (caseData && caseData.id) {
            fetchCaseFiles();
            fetchActivities();
        }
    }, [caseData]);

    const fetchCaseData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            const response = await api.get(`/support-cases/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            setCaseData(response.data);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching case data:', error);

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            setIsLoading(false);
            showPopup({
                title: t('customerSupportCaseDetail.error'),
                message: t('customerSupportCaseDetail.ticketInfoError'),
                type: 'error'
            });
        }
    };

    const fetchCaseFiles = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            const response = await api.get(`/support-case-files/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            // Yanıtı kontrol et ve konsola yazdır

            // API'den gelen verileri düzenleyip dosya adı alanını kontrol edelim
            const formattedFiles = Array.isArray(response.data) ? response.data.map(file => {
                // Dosya adı kontrolü - eğer file_name yok ancak original_name veya başka bir field varsa kullan
                if (!file.file_name || file.file_name === 'undefined') {
                    // Olası dosya adı alanlarını kontrol et
                    file.file_name = file.original_name || file.originalname || file.filename || file.name || `dosya_${file.id}`;
                }
                return file;
            }) : [];

            setFiles(formattedFiles);
        } catch (error) {
            console.error('Error fetching case files:', error);

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            showPopup({
                title: t('customerSupportCaseDetail.error'),
                message: t('customerSupportCaseDetail.fileListError'),
                type: 'error'
            });
        }
    };

    // Fetch case activities
    const fetchActivities = async () => {
        try {
            setIsActivitiesLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            const response = await api.get(`/support-case-activities/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });


            // Format and sort activities by date (newest first)
            const formattedActivities = Array.isArray(response.data)
                ? response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                : [];

            setActivities(formattedActivities);
            setIsActivitiesLoading(false);
        } catch (error) {
            console.error('Error fetching case activities:', error);

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            setIsActivitiesLoading(false);
            // Provide some default activities if API fails
            setActivities([]);
        }
    };

    // Submit new activity
    const submitActivity = async () => {
        if (!activityMessage.trim()) return;

        setIsSubmittingActivity(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            // Net bir şekilde kullanıcı adını alalım
            const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');

            // API'nin beklediği format - kullanıcı bilgilerini doğru şekilde ayarla
            const activityData = {
                case_id: id,
                activity_type: 'COMMENT',
                activity_message: activityMessage.trim(),
                created_by: loggedInUser.id ? parseInt(loggedInUser.id) : 0,
                created_by_name: loggedInUser.full_name || '',
                activity_date: new Date().toISOString(),
                is_visible: 1
            };


            const response = await api.post('/support-case-activities', activityData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data) {
                // Clear input and refresh activities
                setActivityMessage('');
                fetchActivities();

                showPopup({
                    title: t('customerSupportCaseDetail.success'),
                    message: t('customerSupportCaseDetail.commentSaved'),
                    type: 'success'
                });
            }
        } catch (error) {
            console.error('Error submitting activity:', error);

            // Hata detaylarını daha fazla göster
            if (error.response) {
                console.error('Hata detayları:', error.response.data);
                console.error('Hata kodu:', error.response.status);
            }

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            showPopup({
                title: t('customerSupportCaseDetail.error'),
                message: t('customerSupportCaseDetail.commentError', { error: error.response?.data?.message || error.message || 'Bilinmeyen bir hata' }),
                type: 'error'
            });
        } finally {
            setIsSubmittingActivity(false);
        }
    };

    // Get appropriate icon for activity type
    const getActivityIcon = (type) => {
        switch (type) {
            case 'STATUS_CHANGE':
                return <MdCheckCircle size={28} className="text-black dark:text-white" />;
            case 'ASSIGNMENT':
                return <FaUser size={26} className="text-black dark:text-white" />;
            case 'COMMENT':
                return <BsChatRightText size={28} className="text-black dark:text-white" />;
            case 'SYSTEM':
                return <FaHistory size={26} className="text-black dark:text-white" />;
            default:
                return <BsChatRightText size={28} className="text-black dark:text-white" />;
        }
    };

    // Get background color for activity type
    const getActivityColor = (type) => {
        switch (type) {
            case 'STATUS_CHANGE':
                return 'bg-blue-500';
            case 'ASSIGNMENT':
                return 'bg-purple-500';
            case 'COMMENT':
                return 'bg-green-500';
            case 'SYSTEM':
                return 'bg-gray-500';
            default:
                return 'bg-blue-500';
        }
    };

    // Get status name from message
    const getStatusName = (message) => {
        if (message.includes('RESOLVED')) return t('customerSupportCaseDetail.resolved');
        if (message.includes('CLOSED')) return t('customerSupportCaseDetail.closed');
        if (message.includes('OPEN')) return t('customerSupportCaseDetail.open');
        if (message.includes('PENDING')) return t('customerSupportCaseDetail.pending');
        return t('customerSupportCaseDetail.statusChange');
    };

    const handleFileUpload = async (uploadedFiles) => {
        if (!uploadedFiles || uploadedFiles.length === 0) return;

        setIsUploading(true);
        const formData = new FormData();

        // Append each file to the form data
        for (let i = 0; i < uploadedFiles.length; i++) {
            formData.append('files', uploadedFiles[i]);
        }

        // Add support case ID and description
        formData.append('support_case_id', id);
        formData.append('file_description', fileDescription || t('customerSupportCaseDetail.noFileDescription'));

        // Get user info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
        formData.append('uploaded_by', userInfo?.id || '');
        formData.append('uploaded_by_name', userInfo?.full_name || userInfo?.username || '');

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            const response = await api.post('/upload-case-files', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                showPopup({
                    title: t('customerSupportCaseDetail.success'),
                    message: t('customerSupportCaseDetail.filesUploadedSuccess'),
                    type: 'success'
                });

                // Reset file description
                setFileDescription('');

                // Refresh the file list
                fetchCaseFiles();
            } else {
                throw new Error(response.data.message || t('customerSupportCaseDetail.fileUploadFailed'));
            }
        } catch (error) {
            console.error('Error uploading files:', error);

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            showPopup({
                title: t('customerSupportCaseDetail.error'),
                message: t('customerSupportCaseDetail.fileUploadError', { error: error.response?.data?.message || error.message || t('customerSupportCaseDetail.unknownError') }),
                type: 'error'
            });
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFileUpload(e.target.files);
        }
    };

    const openFileSelector = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileDownload = async (fileId, fileName) => {
        try {

            // Dosya adı kontrolü
            if (!fileName || fileName === 'undefined' || fileName === '') {
                console.error('Dosya adı bulunamadı, veritabanından alınacak');
                fileName = `dosya_${fileId}`;
            }

            const token = localStorage.getItem('token');
            if (!token) {
                console.error('Token bulunamadı');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            // İstek yaparken orijinal dosya adını query parametresi olarak gönder
            const response = await api.get(getFileUrl(`/download-case-file/${fileId}?originalName=${encodeURIComponent(fileName)}`), {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob' // Important for file downloads
            });


            // Dosya türünü başlıklardan alma
            const contentType = response.headers['content-type'] || 'application/octet-stream';

            // Dosya adını headers'dan alma (varsa)
            let downloadFileName = fileName;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const fileNameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (fileNameMatch && fileNameMatch[1]) {
                    downloadFileName = fileNameMatch[1].replace(/['"]/g, '');
                }
            }

            // Dosya adını temizleme ve kontrolü
            downloadFileName = downloadFileName.trim();
            if (downloadFileName === 'undefined' || downloadFileName === '') {
                downloadFileName = fileName || `dosya_${fileId}`;
            }

            // Uzantı kontrolü
            if (!downloadFileName.includes('.')) {
                // Dosya uzantısı yoksa, content type'a göre ekleyelim
                const extension = contentType.split('/')[1];
                if (extension && extension !== 'octet-stream') {
                    downloadFileName += '.' + extension;
                }
            }


            // Create blob link to download with correct content type
            const blob = new Blob([response.data], { type: contentType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', downloadFileName);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);

            if (error.response && error.response.status === 403) {
                localStorage.removeItem('token');
                navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                return;
            }

            showPopup({
                title: t('customerSupportCaseDetail.error'),
                message: t('customerSupportCaseDetail.fileDownloadError', { error: error.response?.data?.message || error.message || t('customerSupportCaseDetail.unknownError') }),
                type: 'error'
            });
        }
    };

    const handleFileDelete = async (fileId) => {
        try {
            showPopup({
                title: t('customerSupportCaseDetail.deleteConfirmation'),
                message: t('customerSupportCaseDetail.deleteFileConfirmation'),
                type: 'error',
                showCancel: true,
                onConfirm: async () => {
                    try {
                        setPopup(prev => ({ ...prev, show: false }));

                        const token = localStorage.getItem('token');
                        if (!token) {
                            console.error('Token bulunamadı');
                            navigate('/login', { state: { message: 'Oturumunuz sonlanmış. Lütfen tekrar giriş yapın.' } });
                            return;
                        }

                        const response = await api.delete(`/delete-case-file/${fileId}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });

                        if (response.data.success) {
                            showPopup({
                                title: t('customerSupportCaseDetail.success'),
                                message: t('customerSupportCaseDetail.fileDeletedSuccess'),
                                type: 'success'
                            });

                            // Refresh the file list
                            fetchCaseFiles();
                        } else {
                            throw new Error(response.data.message || t('customerSupportCaseDetail.fileDeleteFailed'));
                        }
                    } catch (error) {
                        console.error('Error deleting file:', error);
                        showPopup({
                            title: t('customerSupportCaseDetail.error'),
                            message: t('customerSupportCaseDetail.fileDeleteError', { error: error.response?.data?.message || error.message || t('customerSupportCaseDetail.unknownError') }),
                            type: 'error'
                        });
                    }
                }
            });
        } catch (error) {
            console.error('Error showing delete confirmation:', error);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileUpload(e.dataTransfer.files);
        }
    };

    const showPopup = (popupData) => {
        setPopup({
            show: true,
            title: popupData.title || t('customerSupportCaseDetail.info'),
            message: popupData.message || '',
            type: popupData.type || 'info',
            showCancel: popupData.showCancel || false,
            onConfirm: popupData.onConfirm || (() => setPopup(prev => ({ ...prev, show: false }))),
            onCancel: popupData.onCancel || (() => setPopup(prev => ({ ...prev, show: false })))
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('customerSupportCaseDetail.notSpecified');

        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                console.error('Invalid date:', dateString);
                return t('customerSupportCaseDetail.invalidDate');
            }

            return new Intl.DateTimeFormat('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (error) {
            console.error('Error formatting date:', error);
            return t('customerSupportCaseDetail.dateError');
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'OPEN':
                return <MdPending className="text-yellow-500" size={20} />;
            case 'IN_PROGRESS':
                return <MdPending className="text-blue-500" size={20} />;
            case 'RESOLVED':
                return <MdCheckCircle className="text-green-500" size={20} />;
            case 'CLOSED':
                return <MdCheckCircle className="text-gray-500" size={20} />;
            case 'CANCELLED':
                return <MdCancel className="text-red-500" size={20} />;
            default:
                return <MdPending className="text-gray-500" size={20} />;
        }
    };

    if (isLoading) {
        return (
            <CustomerLayout>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            </CustomerLayout>
        );
    }

    return (
        <CustomerLayout>
            <div className="container mx-auto py-8 px-4">
                {/* Navigation */}
                <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg mb-4 sm:mb-6">
                    <div className="py-2 sm:py-3 px-3 sm:px-4">
                        <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 text-xs sm:text-sm">
                            <button
                                onClick={() => navigate('/customer-dashboard')}
                                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                                <FaHome className="mr-1" size={12} />
                                <span className="hidden sm:inline">{t('customerSupportCaseDetail.home')}</span>
                                <span className="sm:hidden">Home</span>
                            </button>
                            <span className="text-gray-400 dark:text-gray-500">&gt;</span>
                            <button
                                onClick={() => navigate('/customer-support-cases')}
                                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                                <span className="hidden sm:inline">{t('customerSupportCaseDetail.mySupportTickets')}</span>
                                <span className="sm:hidden">My Support Cases</span>
                            </button>
                            <span className="text-gray-400 dark:text-gray-500">&gt;</span>
                            <span className="text-gray-800 dark:text-gray-200 font-medium truncate">
                                <span className="hidden sm:inline">{t('customerSupportCaseDetail.ticketDetail')} #{caseData.case_id}</span>
                                <span className="sm:hidden">#{caseData.case_id}</span>
                            </span>
                        </div>
                    </div>
                </div>

                                 {/* Popup */}
                 {popup.show && (
                     <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
                         <div className="relative mx-auto w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
                             {/* Header */}
                             <div className="bg-[#272c39] text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 flex justify-between items-center">
                                 <h3 className="text-sm sm:text-base lg:text-lg font-medium">{popup.title}</h3>
                                 <button onClick={popup.onCancel} className="text-white hover:text-gray-200">
                                     <MdClose size={20} className="sm:hidden" />
                                     <MdClose size={24} className="hidden sm:block" />
                                 </button>
                             </div>
                             
                             <div className="p-3 sm:p-4 lg:p-8 bg-white dark:bg-gray-800">
                                 <p className="text-gray-700 dark:text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">{popup.message}</p>
                                 <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                     {popup.showCancel && (
                                         <button
                                             onClick={popup.onCancel}
                                             className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                                         >
                                             {t('customerSupportCaseDetail.cancel')}
                                         </button>
                                     )}
                                     <button
                                         onClick={popup.onConfirm}
                                         className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                                     >
                                         {t('customerSupportCaseDetail.ok')}
                                     </button>
                                 </div>
                             </div>
                         </div>
                     </div>
                 )}

                 {/* Aktivite Ekleme Popup */}
                 {showActivityPopup && (
                     <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
                         <div className="relative mx-auto w-full max-w-md sm:max-w-lg shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
                             {/* Header */}
                             <div className="bg-[#272c39] text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 flex justify-between items-center">
                                 <h3 className="text-sm sm:text-base lg:text-lg font-medium">{t('customerSupportCaseDetail.addActivity')}</h3>
                                 <button
                                     onClick={() => {
                                         setShowActivityPopup(false);
                                         setActivityMessage('');
                                     }}
                                     className="text-white hover:text-gray-200"
                                 >
                                     <MdClose size={20} className="sm:hidden" />
                                     <MdClose size={24} className="hidden sm:block" />
                                 </button>
                             </div>
                             
                             <div className="p-3 sm:p-4 lg:p-8 bg-white dark:bg-gray-800">
                                 <div className="space-y-4 sm:space-y-6">
                                     <div>
                                         <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                                             {t('customerSupportCaseDetail.writeComment')}
                                         </label>
                                         <textarea
                                             value={activityMessage}
                                             onChange={(e) => setActivityMessage(e.target.value)}
                                             placeholder={t('customerSupportCaseDetail.commentPlaceholder')}
                                             className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-xs sm:text-sm"
                                             rows={3}
                                         />
                                     </div>

                                     <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                                         <button
                                             type="button"
                                             onClick={() => {
                                                 setShowActivityPopup(false);
                                                 setActivityMessage('');
                                             }}
                                             className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                                         >
                                             {t('customerSupportCaseDetail.cancel')}
                                         </button>
                                         <button
                                             onClick={async () => {
                                                 if (activityMessage.trim()) {
                                                     await submitActivity();
                                                     setShowActivityPopup(false);
                                                     setActivityMessage('');
                                                 }
                                             }}
                                             disabled={isSubmittingActivity || !activityMessage.trim()}
                                             className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                                         >
                                             {isSubmittingActivity ? (
                                                 <>
                                                     <span className="inline-block h-3 w-3 sm:h-4 sm:w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                                     <span>{t('customerSupportCaseDetail.sending')}</span>
                                                 </>
                                             ) : (
                                                 <>
                                                     <MdSend size={14} className="sm:hidden" />
                                                     <MdSend size={16} className="hidden sm:block" />
                                                     <span>{t('customerSupportCaseDetail.send')}</span>
                                                 </>
                                             )}
                                         </button>
                                     </div>
                                 </div>
                             </div>
                         </div>
                     </div>
                 )}

                {/* Case Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
                    {/* Table Header */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-tl-lg rounded-tr-lg">
                        <h2 className="text-lg font-medium text-gray-800 dark:text-gray-200">{t('customerSupportCaseDetail.caseDetails')}</h2>
                    </div>
                    
                    {/* Desktop Table View */}
                    <div className="hidden lg:block overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.ticketNumber')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.case_id}</td>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.status')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">
                                        <div className="flex items-center">
                                            {getStatusIcon(caseData.case_status)}
                                            <span className="ml-2">{caseData.case_status}</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.createdBy')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.created_by_name || caseData.created_by || t('customerSupportCaseDetail.system')}</td>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.assignedTo')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.assigned_to_name || t('customerSupportCaseDetail.notAssignedYet')}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.creationDate')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{formatDate(caseData.created_at)}</td>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.priority')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.severity || t('customerSupportCaseDetail.notSpecified')}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.product')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.product_name || t('customerSupportCaseDetail.notSpecified')}</td>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.contract')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white">{caseData.contract_name || t('customerSupportCaseDetail.notSpecified')}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.subject')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white" colSpan="3">{caseData.subject}</td>
                                </tr>
                                <tr>
                                    <td className="px-4 py-2 bg-gray-50 dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.detail')}</td>
                                    <td className="px-4 py-2 text-gray-900 dark:text-white whitespace-pre-line" colSpan="3">{caseData.detail}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View - Single Column */}
                    <div className="lg:hidden p-4 space-y-4">
                        {/* Ticket Number */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.ticketNumber')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.case_id}</div>
                        </div>

                        {/* Status */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.status')}</div>
                            <div className="flex items-center">
                                {getStatusIcon(caseData.case_status)}
                                <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.case_status}</span>
                            </div>
                        </div>

                        {/* Created By */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.createdBy')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.created_by_name || caseData.created_by || t('customerSupportCaseDetail.system')}</div>
                        </div>

                        {/* Assigned To */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.assignedTo')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.assigned_to_name || t('customerSupportCaseDetail.notAssignedYet')}</div>
                        </div>

                        {/* Creation Date */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.creationDate')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{formatDate(caseData.created_at)}</div>
                        </div>

                        {/* Priority */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.priority')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.severity || t('customerSupportCaseDetail.notSpecified')}</div>
                        </div>

                        {/* Product */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.product')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.product_name || t('customerSupportCaseDetail.notSpecified')}</div>
                        </div>

                        {/* Contract */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.contract')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.contract_name || t('customerSupportCaseDetail.notSpecified')}</div>
                        </div>

                        {/* Subject */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.subject')}</div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{caseData.subject}</div>
                        </div>

                        {/* Detail */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-300 mb-1">{t('customerSupportCaseDetail.detail')}</div>
                            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">{caseData.detail}</div>
                        </div>
                    </div>
                </div>

                {/* Activities Section */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
                                         <div
                         className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer rounded-tl-lg rounded-tr-lg"
                         onClick={() => setIsActivitiesSectionExpanded(!isActivitiesSectionExpanded)}
                     >
                         <h2 className="text-sm sm:text-base lg:text-lg font-medium text-gray-800 dark:text-gray-200">{t('customerSupportCaseDetail.ticketHistory')}</h2>
                         <div className="flex items-center gap-2 sm:gap-3">
                             {caseData.case_status !== 'CLOSED' && (
                                 <button
                                     onClick={(e) => {
                                         e.stopPropagation();
                                         setShowActivityPopup(true);
                                     }}
                                     className="px-2 sm:px-3 py-1 bg-[#272c39] text-white text-xs sm:text-sm rounded-md hover:bg-[#1e2230] transition-colors duration-200 flex items-center gap-1"
                                 >
                                     <MdComment size={14} className="sm:hidden" />
                                     <MdComment size={16} className="hidden sm:block" />
                                     <span className="hidden sm:inline">{t('customerSupportCaseDetail.addActivity')}</span>
                                 </button>
                             )}
                             <MdExpandMore
                                 size={20}
                                 className={`sm:hidden text-gray-500 dark:text-gray-400 transform transition-transform duration-200 ${isActivitiesSectionExpanded ? 'rotate-180' : ''}`}
                             />
                             <MdExpandMore
                                 size={24}
                                 className={`hidden sm:block text-gray-500 dark:text-gray-400 transform transition-transform duration-200 ${isActivitiesSectionExpanded ? 'rotate-180' : ''}`}
                             />
                         </div>
                     </div>
                    {isActivitiesSectionExpanded && (
                        <div>
                            {/* Activity Feed */}
                            <div className="p-3 sm:p-4 lg:p-6">
                                

                                {/* Aktivitelerin Başlığı ve Ayraç */}
                                {activities.length > 0 && (
                                    <div className="mb-6">
                                        
                                    </div>
                                )}

                                {isActivitiesLoading ? (
                                    <div className="py-8 flex justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : activities.length === 0 ? (
                                    <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                                        <FaHistory className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={36} />
                                        <p className="text-lg font-medium text-gray-500 dark:text-gray-400">{t('customerSupportCaseDetail.noActivitiesYet')}</p>
                                        <p className="text-gray-400 dark:text-gray-500">{t('customerSupportCaseDetail.noOperationsYet')}</p>
                                    </div>
                                ) : (
                                                                         <div className="relative mb-6">
                                         {/* Timeline stem - removed */}

                                        {/* Timeline items */}
                                        <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                                            {activities.map((activity) => (
                                                <div key={activity.id} className="relative">
                                                    {/* Timeline dot/icon - Hidden on all screen sizes */}
                                                    <div className="hidden">
                                                        {getActivityIcon(activity.activity_type)}
                                                    </div>

                                                    {/* Activity card */}
                                                    <div className={`ml-0 rounded-md shadow-sm hover:shadow-md border overflow-hidden transition-all duration-200 ${
                                                        activity.activity_resolution === 1 
                                                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' 
                                                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600'
                                                    }`}>
                                                        {/* Card header */}
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-gray-50 dark:bg-gray-700 px-2 sm:px-3 py-1.5 sm:py-2 border-b border-gray-100 dark:border-gray-600 gap-1 sm:gap-0">
                                                            <div className="flex flex-col sm:flex-row sm:items-center">
                                                                <span className="font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm">
                                                                    {currentUser && activity.created_by &&
                                                                        activity.created_by.toString() === currentUser.id?.toString()
                                                                        ? currentUser.full_name
                                                                        : activity.created_by_name || 'Kullanıcı'}
                                                                </span>
                                                                <span className="hidden sm:inline mx-2 text-gray-300 dark:text-gray-500">•</span>
                                                                <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(activity.created_at)}</span>
                                                            </div>

                                                            <div className="flex justify-end sm:justify-start">
                                                                {/* Mark As Resolved - Priority display */}
                                                                {activity.activity_resolution === 1 && (
                                                                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white flex items-center gap-1">
                                                                        <MdCheckCircle size={12} />
                                                                        {t('customerSupportCaseDetail.markAsResolved')}
                                                                    </div>
                                                                )}

                                                                {activity.activity_type === 'STATUS_CHANGE' && activity.activity_resolution !== 1 && (
                                                                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                        {getStatusName(activity.activity_message)}
                                                                    </div>
                                                                )}

                                                                {activity.activity_type === 'ASSIGNMENT' && activity.activity_resolution !== 1 && (
                                                                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                        {t('customerSupportCaseDetail.assignment')}
                                                                    </div>
                                                                )}

                                                                {activity.activity_type === 'COMMENT' && activity.activity_resolution !== 1 && (
                                                                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                        {t('customerSupportCaseDetail.comment')}
                                                                    </div>
                                                                )}

                                                                {activity.activity_type === 'SYSTEM' && activity.activity_resolution !== 1 && (
                                                                    <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                                        {t('customerSupportCaseDetail.system')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Card body */}
                                                        <div className="px-2 sm:px-3 py-2">
                                                            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line text-xs sm:text-sm">
                                                                {activity.activity_message}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* File Upload Section - Only show if the case is not closed */}
                {caseData.case_status !== 'CLOSED' && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
                        <div
                            className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer rounded-tl-lg rounded-tr-lg"
                            onClick={() => setIsUploadSectionExpanded(!isUploadSectionExpanded)}
                        >
                            <h2 className="text-sm sm:text-base lg:text-lg font-medium text-gray-800 dark:text-gray-200">{t('customerSupportCaseDetail.fileUpload')}</h2>
                            <MdExpandMore
                                size={20}
                                className={`sm:hidden text-gray-500 dark:text-gray-400 transform transition-transform duration-200 ${isUploadSectionExpanded ? 'rotate-180' : ''}`}
                            />
                            <MdExpandMore
                                size={24}
                                className={`hidden sm:block text-gray-500 dark:text-gray-400 transform transition-transform duration-200 ${isUploadSectionExpanded ? 'rotate-180' : ''}`}
                            />
                        </div>
                        {isUploadSectionExpanded && (
                            <div>
                                <div className="p-3 sm:p-4 lg:p-6">
                                    <div className="mb-3 sm:mb-4">
                                        <label htmlFor="fileDescription" className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {t('customerSupportCaseDetail.fileDescription')}
                                        </label>
                                        <input
                                            type="text"
                                            id="fileDescription"
                                            value={fileDescription}
                                            onChange={(e) => setFileDescription(e.target.value)}
                                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-xs sm:text-sm"
                                            placeholder={t('customerSupportCaseDetail.enterFileDescription')}
                                        />
                                    </div>

                                    <div
                                        className={`border-2 border-dashed rounded-md p-3 sm:p-4 lg:p-6 ${dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                                            }`}
                                        onDragEnter={handleDrag}
                                        onDragOver={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDrop={handleDrop}
                                    >
                                        <div className="text-center">
                                            <MdUpload className="mx-auto h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-gray-400 dark:text-gray-500" />
                                            <div className="mt-2">
                                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                                    {t('customerSupportCaseDetail.dragFilesHere')}
                                                </p>
                                                <button
                                                    type="button"
                                                    className="mt-2 inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                    onClick={openFileSelector}
                                                    disabled={isUploading}
                                                >
                                                    {isUploading ? t('customerSupportCaseDetail.uploading') : t('customerSupportCaseDetail.selectFile')}
                                                </button>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    multiple
                                                    className="hidden"
                                                    onChange={handleFileInputChange}
                                                    disabled={isUploading}
                                                />
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                PDF, Word, Excel, PowerPoint, ZIP, TXT, LOG (max. 10MB)
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Files Table */}
                                <div className="border-t border-gray-200 dark:border-gray-600">
                                    <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                        <h2 className="text-sm sm:text-base lg:text-lg font-medium text-gray-800 dark:text-gray-200">{t('customerSupportCaseDetail.uploadedFiles')}</h2>
                                    </div>
                                    
                                    {/* Desktop Table View */}
                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {t('customerSupportCaseDetail.fileName')}
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {t('customerSupportCaseDetail.description')}
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {t('customerSupportCaseDetail.uploadedBy')}
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {t('customerSupportCaseDetail.uploadDate')}
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                        {t('customerSupportCaseDetail.actions')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                {files.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400 rounded-bl-lg rounded-br-lg">
                                                            {t('customerSupportCaseDetail.noFilesUploadedYet')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    files.map((file) => (
                                                        <tr key={file.id}>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 rounded-bl-lg">
                                                                {file.file_name}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                {file.file_description || t('customerSupportCaseDetail.noDescription')}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                {file.uploaded_by_name || caseData.created_by_name || t('customerSupportCaseDetail.unknown')}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                                {formatDate(file.uploaded_at)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium rounded-br-lg">
                                                                <button
                                                                    onClick={() => handleFileDownload(file.id, file.file_name)}
                                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3 "
                                                                    title={t('customerSupportCaseDetail.download')}
                                                                >
                                                                    <MdDownload size={20} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleFileDelete(file.id)}
                                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 "
                                                                    title={t('customerSupportCaseDetail.delete')}
                                                                >
                                                                    <MdDelete size={20} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Card View */}
                                    <div className="lg:hidden p-3 sm:p-4">
                                        {files.length === 0 ? (
                                            <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                                                {t('customerSupportCaseDetail.noFilesUploadedYet')}
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {files.map((file) => (
                                                    <div key={file.id} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg border border-gray-200 dark:border-gray-600">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div className="flex-1 min-w-0">
                                                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                                    {file.file_name}
                                                                </h3>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                    {file.file_description || t('customerSupportCaseDetail.noDescription')}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center gap-2 ml-2">
                                                                <button
                                                                    onClick={() => handleFileDownload(file.id, file.file_name)}
                                                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1"
                                                                    title={t('customerSupportCaseDetail.download')}
                                                                >
                                                                    <MdDownload size={18} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleFileDelete(file.id)}
                                                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                                                                    title={t('customerSupportCaseDetail.delete')}
                                                                >
                                                                    <MdDelete size={18} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-xs text-gray-500 dark:text-gray-400 gap-1">
                                                            <span>{t('customerSupportCaseDetail.uploadedBy')}: {file.uploaded_by_name || caseData.created_by_name || t('customerSupportCaseDetail.unknown')}</span>
                                                            <span>{formatDate(file.uploaded_at)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </CustomerLayout>
    );
};

export default CustomerSupportCaseDetailPage;